import { RadioButton, ReactEasyForm } from "@aeco-cloud/react-easy-form";
import { Button } from "@material-ui/core";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import settings from "@styles/settings";
import { toDate } from "date-fns";
import lastDayOfQuarter from "date-fns/lastDayOfQuarter";
import { useContext, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { TextContext } from "../../../pages/bepaal-statuut";
import { Label, NavigationButtons, Question, Section } from "../../form-layout";
import StepBody from "../../form-layout/StepBody";
import Markdown from "../../Markdown";
import { SendContext, StateContext } from "../BepaalStatuut";
import {
  getEmploymentRate,
  getPerformedHours,
  isActive,
} from "../helpers/performedHours/functions";
import holidays from "../helpers/performedHours/holidays";
import { Data } from "../helpers/performedHours/types";
import Contract from "./components/Contract";
import { SituationData } from "./Situation";
import { Interval } from "date-fns";
import schoolVakantiesUren from "../helpers/performedHours/schoolVakantiesUren";

const PerformedHours = () => {
  const send = useContext(SendContext);
  const state = useContext(StateContext);
  const text = useContext(TextContext);
  const [contractNumber, setContractNumber] = useState(1);
  const [contractArray, setContractArray] = useState([1]);
  const form = useForm();

  const year = state?.context?.memory.quarter?.quarterYear ?? 2020;
  const quarter = state?.context?.memory.quarter?.quarterNumber ?? 1;

  const quarterStart = new Date(year, (quarter - 1) * 3, 1);
  const quarterEnd = lastDayOfQuarter(quarterStart);
  const quarterInterval = { start: quarterStart, end: quarterEnd } as Interval;

  const situation: SituationData | undefined = state?.context?.memory.situation;

  // the standard condition to match
  const condition = {
    employmentRate: 0.5,
    performedHours: 235,
  };

  if (situation?.situation === "civilServant" && situation?.type === "education") {
    condition.employmentRate = 0.6;
    condition.performedHours = 282;
  }

  if (situation?.situation === "employee" && situation?.type === "teacher") {
    condition.employmentRate = 0.5;
    condition.performedHours = 235 - schoolVakantiesUren[year][quarter];
    console.log(
      "number of hours schoolvakantie for employee + teacher: ",
      schoolVakantiesUren[year][quarter]
    );
  }

  if (situation?.type === "terminationFee" || situation?.type === "severancePayment") {
    condition.employmentRate = 0.5;
    condition.performedHours = 0; // no condition here
  }

  const onSubmit: SubmitHandler<any> = (data) => {
    console.log(data);

    // parse the contract data so that each contract is an object with the same keys
    const contractDataArray = [];
    contractArray.forEach((contractNumber) => {
      const contractData = {};
      for (const [key, value] of Object.entries(data)) {
        if (key.includes("contract" + contractNumber)) {
          contractData[key.split("_").slice(-1)[0]] = value;
        }
      }
      // if no endDate, change null to end of quarter
      if (contractData["endDate"] === null) {
        contractData["endDate"] = quarterEnd;
      }

      // convert the datetimes to dates
      contractData["endDate"] = toDate(contractData["endDate"]);
      contractData["startDate"] = toDate(contractData["startDate"]);

      // put the start and enddate in an interval
      contractData["interval"] = { start: contractData["startDate"], end: contractData["endDate"] };

      // convert employmentRateDenominator and employmentRateNumerator to number
      contractData["employmentRateDenominator"] = Number(contractData["employmentRateDenominator"]);
      contractData["employmentRateNumerator"] = Number(contractData["employmentRateNumerator"]);

      // if allDays === "true", append the data with performedHours = (numberOfWorkingDays x employmentRateDenominator)/5
      if (contractData["allDays"] === "true") {
        contractData["performedHours"] = null;
      }
      contractDataArray.push(contractData);
    });

    const transformedData = {
      quarter: quarterInterval,
      zibInPreviousQuarter: false,
      contractDataArray: contractDataArray,
      contractEmploymentRateCondition: condition.employmentRate ?? 0.5
    } as Data;

    console.log("All data: ", transformedData)

    // check if active
    let active = isActive(transformedData, holidays);

    // check employment rate
    let employmentRate = getEmploymentRate(transformedData);

    // check performed hours
    let performedHours = getPerformedHours(transformedData);

    // if educationDeferredRenumeration -> add halftijds for juli and augustus (aanpassingen met Annelies van 21 october 2022)
    if (situation?.educationDeferredRenumeration === "true"){
      console.log("uitgestelde bezoldiging detected!")
      active = true
      employmentRate = employmentRate + 0.354
      performedHours = performedHours + 156.666
    }

    const contractResults = {
      active: active,
      employmentRate: employmentRate,
      performedHours: performedHours,
    };

    console.log("contractResults: " , contractResults)

    let result = "secondaryActivityFalse";
    if (contractResults.active) {
      if (
        contractResults.employmentRate >= condition.employmentRate &&
        contractResults.performedHours >= condition.performedHours
      ) {
        result = "secondaryActivityTrue";
      }
    }

    data["result"] = result;
    data["contractResults"] = contractResults;
    data["condition"] = condition;

    send({ type: "NEXT", data: data });
  };

  const onBack = () => {
    send({ type: "BACK" });
  };

  const addContract = () => {
    const newContractNumber = contractNumber + 1;
    setContractNumber(newContractNumber);
    setContractArray([...contractArray, newContractNumber]);
  };

  const deleteContract = (contractNumber: number) => {
    const newContractArray = contractArray.filter((contract) => contract !== contractNumber);
    setContractArray(newContractArray);
  };

  return (
    <ReactEasyForm form={form}>
      <StepBody>
        <Section>
          <Question>
            <Label label={text.performedHours_zibInPreviousQuarter_label} />
            <RadioButton
              name="zibInPreviousQuarter"
              minButtonWidth="240px"
              {...settings.selectButtonsProps}
              options={[
                { value: "true", label: text.general_yes },
                { value: "false", label: text.general_no },
              ]}
              rules={{
                required: text.general_requiredQuestion,
              }}
            />
          </Question>
        </Section>
        <Section>
          {/* <div style={{ margin: "20px 0px 20px 0px", width: "100%", textAlign: "center" }}>
            <Image src="/images/undraw_contract.svg" height="200px" width="200px" />
          </div> */}
          <Markdown>{text.performedHours_contractFillOutInfo}</Markdown>
        </Section>
        <div>
          {contractArray.map((number) => {
            return (
              <Contract
                form={form}
                contractNumber={number}
                key={number}
                contractArray={contractArray}
                deleteContract={deleteContract}
                quarterInterval={quarterInterval}
              />
            );
          })}
        </div>
        <div style={{ width: "100%", textAlign: "left", marginBottom: "40px" }}>
          {" "}
          <Button
            variant="text"
            color="secondary"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => {
              addContract();
            }}
          >
            {text.performedHours_addContractButton}
          </Button>
        </div>
      </StepBody>
      <NavigationButtons onBack={onBack} onNext={form.handleSubmit(onSubmit)} />
    </ReactEasyForm>
  );
};
export default PerformedHours;
